import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * A block that GLIDES in when it appears and GLIDES out before it leaves.
 *
 * The app's standing rule (Vinnie, 28 Oct): nothing pops in or out — every change
 * from one state to another, expansion or contraction, animates. `Expander` already
 * does this for a panel that stays mounted and toggles `open`. This does it for a
 * thing that is CONDITIONALLY RENDERED — an optional card field that recedes to a
 * chip, a section revealed by a "+" chip — where the element is truly added to and
 * removed from the tree.
 *
 * HOW
 * - On the transition hidden → shown, the measured height animates 0 → auto.
 * - On shown → hidden, it animates the current height → 0, THEN unmounts (so the
 *   contraction is seen rather than the element just vanishing).
 * - A field that is ALREADY shown when the card opens does NOT animate — only a
 *   genuine reveal/remove does — so opening a populated card is still calm. This is
 *   told apart by the value of `show` at creation (present-on-open = true).
 *
 * Height is measured in the browser and released to `auto` when it settles, the same
 * approach as Expander (a max-height guess clips or eases against empty space). The
 * children's own popovers portal out, so `overflow: hidden` while animating never
 * clips a dropdown.
 */
export default function Collapsible({
  show,
  children,
  className,
}: {
  show: boolean;
  children: ReactNode;
  className?: string;
}) {
  const [render, setRender] = useState(show);
  const ref = useRef<HTMLDivElement>(null);
  const firstRun = useRef(true);
  const bornShown = useRef(show);

  // Bring it into the tree the instant it should show; removal waits for the
  // shrink to finish (below).
  useEffect(() => {
    if (show) setRender(true);
  }, [show]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !render) return;

    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    // A field that was already shown when the card opened settles to its height
    // with no animation — only a real reveal or remove glides.
    if (firstRun.current) {
      firstRun.current = false;
      if (show && bornShown.current) {
        el.style.height = 'auto';
        return;
      }
    }

    if (reduce) {
      if (show) el.style.height = 'auto';
      else setRender(false);
      return;
    }

    let settled = false;
    const clearTimer = { id: 0 as number | ReturnType<typeof setTimeout> };

    if (show) {
      const target = el.scrollHeight;
      el.style.overflow = 'hidden';
      el.style.height = '0px';
      void el.offsetHeight; // reflow, so the browser starts from 0
      el.style.transition = 'height var(--ease-slow)';
      requestAnimationFrame(() => {
        if (ref.current) ref.current.style.height = `${target}px`;
      });
      const release = () => {
        if (settled) return;
        settled = true;
        if (ref.current) {
          ref.current.style.height = 'auto';
          ref.current.style.overflow = '';
          ref.current.style.transition = '';
        }
      };
      const onEnd = (e: TransitionEvent) => {
        if (e.propertyName === 'height') release();
      };
      el.addEventListener('transitionend', onEnd);
      clearTimer.id = setTimeout(release, 500);
      return () => {
        el.removeEventListener('transitionend', onEnd);
        clearTimeout(clearTimer.id);
      };
    }

    // Shrinking away, then unmount.
    el.style.overflow = 'hidden';
    el.style.height = `${el.scrollHeight}px`;
    void el.offsetHeight;
    el.style.transition = 'height var(--ease-slow)';
    requestAnimationFrame(() => {
      if (ref.current) ref.current.style.height = '0px';
    });
    const finish = () => {
      if (settled) return;
      settled = true;
      setRender(false);
    };
    const onEnd = (e: TransitionEvent) => {
      if (e.propertyName === 'height') finish();
    };
    el.addEventListener('transitionend', onEnd);
    clearTimer.id = setTimeout(finish, 500);
    return () => {
      el.removeEventListener('transitionend', onEnd);
      clearTimeout(clearTimer.id);
    };
  }, [show, render]);

  if (!render) return null;
  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
